/**
 * Internationalization support for Therappi®
 * Currently supports: English, Spanish, French, German, Italian
 */

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
] as const;

export type LanguageCode = typeof SUPPORTED_LANGUAGES[number]['code'];

/**
 * Get browser's preferred language
 */
export const getBrowserLanguage = (): LanguageCode => {
  const browserLang = navigator.language.split('-')[0];
  const supported = SUPPORTED_LANGUAGES.find(l => l.code === browserLang);
  return supported ? supported.code : 'en';
};

/**
 * Save language preference
 */
export const saveLanguagePreference = (language: LanguageCode) => {
  localStorage.setItem('therappi_language', language);
  document.documentElement.lang = language;
};

/**
 * Load language preference
 */
export const loadLanguagePreference = (): LanguageCode => {
  const saved = localStorage.getItem('therappi_language') as LanguageCode;
  return saved || getBrowserLanguage();
};

/**
 * Basic translations for common UI elements
 * Full translation system to be implemented with i18next or similar
 */
export const translations = {
  en: {
    welcome: 'Welcome',
    settings: 'Settings',
    profile: 'Profile',
    subscription: 'Subscription',
    logout: 'Log Out',
    save: 'Save',
    cancel: 'Cancel',
  },
  es: {
    welcome: 'Bienvenido',
    settings: 'Configuración',
    profile: 'Perfil',
    subscription: 'Suscripción',
    logout: 'Cerrar Sesión',
    save: 'Guardar',
    cancel: 'Cancelar',
  },
  fr: {
    welcome: 'Bienvenue',
    settings: 'Paramètres',
    profile: 'Profil',
    subscription: 'Abonnement',
    logout: 'Se Déconnecter',
    save: 'Enregistrer',
    cancel: 'Annuler',
  },
  de: {
    welcome: 'Willkommen',
    settings: 'Einstellungen',
    profile: 'Profil',
    subscription: 'Abonnement',
    logout: 'Abmelden',
    save: 'Speichern',
    cancel: 'Abbrechen',
  },
  it: {
    welcome: 'Benvenuto',
    settings: 'Impostazioni',
    profile: 'Profilo',
    subscription: 'Abbonamento',
    logout: 'Esci',
    save: 'Salva',
    cancel: 'Annulla',
  },
};

export const t = (key: keyof typeof translations['en'], lang: LanguageCode = 'en'): string => {
  return translations[lang][key] || translations['en'][key];
};
